import { IGetListService } from "../../abstractions/services/actions/IGetListService";
import {
  grpcCatalogosDatabase,
  GrpcCatalogoItem,
  GrpcCatalogoStoreName,
} from "../../comun/database/GrpcCatalogosDatabase";
import { sortByField } from "../../utils/sortUtils";
import { serviceSync } from "./ServiceSync";

/** Resultado crudo de pedir un catálogo por gRPC: los items ya listos para
 *  guardar (si el catálogo tiene geometrías u otro campo que decodificar,
 *  la subclase lo resuelve en `fetchAsync` antes de devolverlos acá — el
 *  timestamp de sincronización que devuelve el server. */
export type GrpcCatalogoFetchResult<TItem extends GrpcCatalogoItem> = {
  items: TItem[];
  timestampSincronizacion: bigint;
};

/** Servicio base para los 9 catálogos migrados a gRPC-Web (ver CLAUDE.md del
 *  Server, sección migración a gRPC-Web). Homólogo de `BaseGetCatalogService`
 *  (REST, sin delta) y `BaseGetListService` (REST con delta, para entidades
 *  no-catálogo).
 *
 *  **Sincroniza una sola vez, no por cooldown** (2026-09-15, ver `syncAsync`):
 *  el server sigue soportando delta por `ultima_sincronizacion` -queda el
 *  mecanismo armado, por si algún día hace falta reactivarlo-, pero el
 *  cliente ya no lo usa mientras el store tenga datos. `getListAsync()`
 *  sincroniza (si corresponde) y devuelve desde IndexedDB. No hace falta
 *  llamarlo antes de leer: `BaseInit` dispara `syncAsync()` de los 9
 *  catálogos apenas termina el login/checkToken, sin bloquear la navegación
 *  (ver `syncAllCatalogsInBackground` en `catalogSync.ts`). */
export abstract class BaseGrpcCatalogService<TItem extends GrpcCatalogoItem>
  implements IGetListService<TItem>
{
  /** Store de `GrpcCatalogosDatabase` para este catálogo (también la clave de
   *  cooldown/dedupe de `ServiceSync`). */
  protected abstract readonly storeName: GrpcCatalogoStoreName;

  /** Campo por el que se ordena el catálogo (ascendente). */
  protected abstract readonly sortFieldName: string;

  /** Llama al RPC del catálogo y devuelve los items ya en su forma final para
   *  guardar (con geometrías decodificadas si aplica) más el nuevo
   *  watermark. */
  protected abstract fetchAsync(ultimaSincronizacion: bigint): Promise<GrpcCatalogoFetchResult<TItem>>;

  /** Sincroniza **una sola vez**: si el store ya tiene datos (de un login
   *  anterior, incluso de hace meses), no vuelve a pegarle al server —
   *  pedido explícito, 2026-09-15 (antes reintentaba en cada carga de
   *  página, porque el cooldown de `ServiceSync` es en memoria y no
   *  sobrevive un reload). La única forma de forzar una resincronización es
   *  subir `DB_VERSION` de `GrpcCatalogosDatabase` (borra todos los stores),
   *  no un TTL. No devuelve nada: quien necesite los datos llama
   *  `getListAsync()`. */
  async syncAsync(): Promise<void> {
    const yaTieneDatos = await grpcCatalogosDatabase.hasDataAsync(this.storeName);
    if (yaTieneDatos) return;

    await serviceSync.run(`grpc:${this.storeName}`, async () => {
      const ultimaSincronizacion = await grpcCatalogosDatabase.getUltimaSincronizacionAsync(this.storeName);
      const resultado = await this.fetchAsync(ultimaSincronizacion);
      await grpcCatalogosDatabase.putDeltaAsync(this.storeName, resultado.items, resultado.timestampSincronizacion);
    });
  }

  /** Sincroniza (si corresponde) y devuelve el catálogo completo desde
   *  IndexedDB, ordenado por `sortFieldName`. */
  async getListAsync(): Promise<TItem[]> {
    await this.syncAsync();
    const items = await grpcCatalogosDatabase.getListAsync<TItem>(this.storeName);
    return sortByField(items, this.sortFieldName);
  }

  /** Cumple `IGetListService`, pero sin efecto práctico para estos catálogos
   *  (2026-09-15): la puerta real de `syncAsync` es `hasDataAsync` sobre
   *  IndexedDB, no el cooldown de `ServiceSync` que esto resetea — con el
   *  store ya poblado, `syncAsync` corta antes de llegar a `serviceSync.run`.
   *  Nada llama `clearCache()` sobre estos 9 catálogos hoy (son de solo
   *  lectura); si algún día hiciera falta forzar una resincronización real,
   *  hay que borrar el store (`grpcCatalogosDatabase`), no resetear esto. */
  clearCache(): void {
    serviceSync.reset(`grpc:${this.storeName}`);
  }
}
